import React from 'react';

import cx from 'classnames';
import { toPairs } from 'ramda';
import { Controller, FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Alert } from 'components/shared/Alert';
import { BaseRadioButtonGroup } from 'components/shared/BaseFormComponents/RadioButton/RadioButtonGroup';
import { BaseSelect } from 'components/shared/BaseFormComponents/Select';
import Button from 'components/shared/Button';
import Visibility from 'components/shared/Icon/icons/Visibility';
import { PAGE_TYPE_AREA } from 'constants/index';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { areaEventTypeToName } from 'detailsv2/structure';
import { showErrorSnackbar } from 'hooks/useSnackbar';
import { canCreateNewContentPageSelector } from 'store/user/selectors';
import { createOptionFromPageV2 } from 'utils/createOptionFromPage';

import { getOptionsLoader } from '../../functions';
import cs from '../../index.module.css'; // TODO
import { handleSelectPage, openPagePreview } from '../functions';
import { useSuggestedAreaForm } from './hooks';
import s from './index.module.css';
import { TProps } from './types';

export const SuggestedAreaForm = function ({ onSubmit }: TProps) {
  const { methods, nightsCount, isDisabled } = useSuggestedAreaForm();

  const { control } = methods;
  const loadAreaOptions = getOptionsLoader([PAGE_TYPE_AREA], createOptionFromPageV2);
  const isCreateDisabled = useSelector(canCreateNewContentPageSelector);

  const areaTypeOptions = React.useMemo(() => {
    const baseOptions = toPairs(areaEventTypeToName).map(([value, label]) => ({ id: value, name: label }));
    if (nightsCount === 2) {
      return baseOptions.map((option) => {
        if (option.id === 'multi_day') return { ...option, name: 'As an Activity Event' };
        if (option.id === 'in_day') return { ...option, name: 'On Check in Day Only' };
        return option;
      });
    }
    if (nightsCount >= 2) return baseOptions;
    return baseOptions.filter(({ id }) => id === 'in_day');
  }, [nightsCount]);

  const handleSubmit = methods.handleSubmit((values) => {
    try {
      const { attachedPage } = values;
      if (!attachedPage) throw new Error('Invalid form data');

      onSubmit(values);
    } catch {
      showErrorSnackbar(GENERAL_ERROR_MESSAGE);
    }
  });

  return (
    <FormProvider {...methods}>
      <form className={cs.form}>
        <Controller
          name="attachedPage"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <div>
                <BaseSelect
                  placeholder="Search Content Library"
                  isClearable
                  defaultOption={value}
                  loadOptions={loadAreaOptions}
                  onChange={handleSelectPage(onChange)}
                  errorMessage={error ? error.message : ''}
                  optionActionIcon={Visibility}
                  onOptionActionClick={openPagePreview}
                  isCreatable
                  isCreateDisabled={isCreateDisabled}
                />
              </div>
            );
          }}
        />

        <div className={cx(cs.title, s.infoNotes)}>Display Settings</div>
        <div className={s.notes}>How would you like to display this area page?</div>

        <Controller
          name="areaType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <div>
              <BaseRadioButtonGroup value={value} onChange={onChange} options={areaTypeOptions} />
            </div>
          )}
        />

        <Alert
          className={s.infoBlock}
          variant="info"
          content={
            <>
              For safaris and beach holidays, or anytime there are no other events planned on the interim days, using
              the area page as a multi-day activity provides relevant content and context for otherwise empty days.
            </>
          }
        />

        <div className={cs.footer}>
          <Button onClick={handleSubmit} disabled={isDisabled} className={cs.buttonText} size="large" htmlType="button">
            Select & Add
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
